import type { AlarmConfig, InterlockConfig, LoopConfig } from '../../engine/plant/types';

export const loops: LoopConfig[] = [
  {
    id: 'FIC-101',
    name: 'Crude charge',
    description: 'Unit rate. Everything downstream is sized against it.',
    guidance:
      'Charge rate sets the vapour load in the tower, the duty the heater has to put in, and ' +
      'the residence time in every exchanger. Move it slowly — the column takes fifteen ' +
      'minutes to tell you what it thinks of the change.',
    pv: 'FT-101.PV',
    out: 'FV-101.CMD',
    sp: 500,
    mode: 'AUTO',
    badPv: 'shedToMan',
    tuning: { kc: 0.6, ti: 14, td: 0, direction: 'reverse', spLo: 150, spHi: 700, opLo: 15, opHi: 100 },
  },
  {
    id: 'TIC-201',
    name: 'Heater coil outlet',
    description: 'Cascade primary — its output is the fuel-gas flow setpoint.',
    guidance:
      'The coil outlet sets the flash in the tower and therefore every cut point. It is the ' +
      'single most important temperature on the unit. Watch the tube skin alongside it: skin ' +
      'temperature is what the trip protects, and it moves faster than the outlet does.',
    pv: 'TT-201.PV',
    out: 'TIC-201.OP',
    sp: 623,
    mode: 'AUTO',
    cascade: { secondary: 'FIC-202' },
    badPv: 'shedToMan',
    tuning: { kc: 2.4, ti: 180, td: 20, dFilterN: 8, direction: 'reverse', spLo: 560, spHi: 660, opLo: 0, opHi: 100 },
  },
  {
    id: 'FIC-202',
    name: 'Fuel gas flow',
    description: 'Cascade secondary under the coil-outlet controller.',
    guidance:
      'The inner loop exists to reject fuel-gas header pressure swings before they ever reach ' +
      'the coil. If you take it out of CAS, every burp on the fuel main lands on your ' +
      'temperature.',
    pv: 'FT-202.PV',
    out: 'FV-202.CMD',
    sp: 0.62,
    mode: 'CAS',
    badPv: 'shedToMan',
    tuning: { kc: 0.5, ti: 10, td: 0, direction: 'reverse', spLo: 0, spHi: 1.2, opLo: 0, opHi: 100 },
  },
  {
    id: 'PIC-301',
    name: 'Column pressure',
    description: 'Condenser cooling-water valve holds the tower pressure.',
    guidance:
      'DIRECT acting: pressure up calls for more cooling. Pressure moves every cut point — ' +
      'roughly 25 K per e-fold — so a wandering pressure controller shows up as wandering ' +
      'product quality hours later. Losing the condenser entirely is a pressure runaway.',
    pv: 'PT-301.PV',
    out: 'FV-301.CMD',
    sp: 1.8,
    mode: 'AUTO',
    badPv: 'shedToMan',
    tuning: { kc: 4, ti: 90, td: 0, direction: 'direct', spLo: 1.2, spHi: 3, opLo: 0, opHi: 100 },
  },
  {
    id: 'LIC-302',
    name: 'Reflux drum level',
    description: 'Naphtha product valve holds the drum level.',
    guidance:
      'The drum is small — twenty minutes of hold-up. Whatever the condenser makes has to ' +
      'leave as reflux or as product, and reflux is spoken for by the top temperature ' +
      'controller. That leaves the naphtha valve to take up the slack.',
    pv: 'LT-302.PV',
    out: 'FV-304.CMD',
    sp: 50,
    mode: 'AUTO',
    badPv: 'shedToMan',
    tuning: { kc: 2.5, ti: 500, td: 0, direction: 'direct', spLo: 20, spHi: 80, opLo: 0, opHi: 100 },
  },
  {
    id: 'TIC-304',
    name: 'Column top temperature',
    description: 'Cascade primary — its output is the reflux flow setpoint.',
    guidance:
      'DIRECT acting: a rising top temperature calls for more reflux. More reflux means a ' +
      'sharper naphtha endpoint and more condenser duty. This is the loop that trades energy ' +
      'for product quality, and it is where most of the money on a crude unit is.',
    pv: 'TT-304.PV',
    out: 'TIC-304.OP',
    sp: 393,
    mode: 'AUTO',
    cascade: { secondary: 'FIC-303' },
    badPv: 'shedToMan',
    tuning: { kc: 3, ti: 420, td: 0, direction: 'direct', spLo: 370, spHi: 420, opLo: 0, opHi: 100 },
  },
  {
    id: 'FIC-303',
    name: 'Reflux flow',
    description: 'Cascade secondary under the top temperature controller.',
    pv: 'FT-303.PV',
    out: 'FV-303.CMD',
    sp: 200,
    mode: 'CAS',
    badPv: 'shedToMan',
    guidance: 'A fast flow loop. Its only job is to make the reflux follow its setpoint cleanly.',
    tuning: { kc: 0.6, ti: 12, td: 0, direction: 'reverse', spLo: 0, spHi: 400, opLo: 0, opHi: 100 },
  },
  {
    id: 'FIC-305',
    name: 'Kerosene draw',
    description: 'Side-draw rate.',
    guidance:
      'Drawing more kerosene pulls the cut deeper into the diesel range: the kerosene gets ' +
      'heavier and the diesel lighter. Every side draw is a trade against its neighbour.',
    pv: 'FT-305.PV',
    out: 'FV-305.CMD',
    sp: 90,
    mode: 'AUTO',
    badPv: 'shedToMan',
    tuning: { kc: 0.6, ti: 14, td: 0, direction: 'reverse', spLo: 0, spHi: 180, opLo: 0, opHi: 100 },
  },
  {
    id: 'FIC-306',
    name: 'Diesel draw',
    description: 'Side-draw rate.',
    guidance:
      'The diesel 95 % point is the quality that matters here, and it responds to the draw ' +
      'rate, the coil outlet and the pressure. The analyser is three minutes behind — do not ' +
      'chase it.',
    pv: 'FT-306.PV',
    out: 'FV-306.CMD',
    sp: 125,
    mode: 'AUTO',
    badPv: 'shedToMan',
    tuning: { kc: 0.6, ti: 14, td: 0, direction: 'reverse', spLo: 0, spHi: 260, opLo: 0, opHi: 100 },
  },
  {
    id: 'LIC-307',
    name: 'Column bottoms level',
    description: 'Residue valve holds the bottoms inventory.',
    guidance:
      'The bottoms level closes the whole unit’s material balance. If it climbs, more is ' +
      'coming in than is going out — check the charge rate against the sum of the draws.',
    pv: 'LT-307.PV',
    out: 'FV-307.CMD',
    sp: 50,
    mode: 'AUTO',
    badPv: 'shedToMan',
    tuning: { kc: 2.5, ti: 600, td: 0, direction: 'direct', spLo: 20, spHi: 80, opLo: 0, opHi: 100 },
  },
];

const g = (cause: string, consequence: string, action: string): Pick<AlarmConfig, 'cause' | 'consequence' | 'action'> => ({ cause, consequence, action });

export const alarms: AlarmConfig[] = [
  {
    id: 'TT-202.H',
    tag: 'TT-202.PV',
    kind: 'H',
    limit: 723,
    deadband: 5,
    onDelay: 10,
    priority: 'high',
    text: 'Heater tube-skin temperature high',
    ...g(
      'Firing too hard for the charge rate, coke on the inside of the tubes, or a burner out of balance.',
      'Tube metal creeps and eventually ruptures. Skin temperature, not coil outlet, is what limits the heater.',
      'Cut the firing or raise the charge rate. If the skin is high at normal firing, the tubes are fouling and the unit needs a decoke.',
    ),
  },
  {
    id: 'TT-202.HH',
    tag: 'TT-202.PV',
    kind: 'HH',
    limit: 753,
    deadband: 5,
    onDelay: 3,
    priority: 'emergency',
    text: 'Heater tube-skin very high — trip',
    ...g('Sustained over-firing or loss of charge flow.', 'Tube rupture and a fire in the firebox.', 'The heater will trip. Do not reset until you know why the skin was hot.'),
  },
  {
    id: 'FT-101.L',
    tag: 'FT-101.PV',
    kind: 'L',
    limit: 260,
    deadband: 15,
    onDelay: 10,
    priority: 'high',
    text: 'Crude charge flow low',
    ...g(
      'Charge pump trip, valve closed, or suction lost.',
      'A fired heater with no flow through the coil is the classic way to rupture a tube. Skin temperature rises within seconds.',
      'Cut the firing immediately and restore flow. The low-flow interlock will trip the heater if you do not.',
    ),
  },
  {
    id: 'H-201.FLAME',
    tag: 'H-201.FLAME',
    kind: 'digital',
    digitalState: 0,
    onDelay: 2,
    priority: 'emergency',
    text: 'Heater flame failure',
    ...g('Loss of fuel-gas pressure, a pilot out, or a burner blowing off.', 'Unburnt fuel in a hot firebox. The fuel must be isolated before any attempt to relight.', 'Confirm the fuel valve has shut. Purge before relighting; never relight into a hot box on a hunch.'),
  },
  {
    id: 'PT-301.H',
    tag: 'PT-301.PV',
    kind: 'H',
    limit: 2.15,
    deadband: 0.05,
    onDelay: 20,
    priority: 'high',
    text: 'Column pressure high',
    ...g(
      'Condenser duty down — warm cooling water, low cooling-water flow, or fouling — or more vapour than the condenser was sized for.',
      'Every cut point moves up with pressure, so the products drift off spec. Above 2.6 bara the relief lifts.',
      'Check PIC-301 is in AUTO with the cooling-water valve responding. If the condenser is limited, reduce charge or coil outlet.',
    ),
  },
  {
    id: 'PT-301.HH',
    tag: 'PT-301.PV',
    kind: 'HH',
    limit: 2.6,
    deadband: 0.05,
    onDelay: 5,
    priority: 'emergency',
    text: 'Column pressure very high',
    ...g('Condenser lost.', 'The tower relief lifts to flare and the unit is out of containment.', 'The interlock will cut the firing. Restore cooling before you restart.'),
  },
  {
    id: 'PDT-301.H',
    tag: 'PDT-301.PV',
    kind: 'H',
    limit: 0.55,
    deadband: 0.03,
    onDelay: 30,
    priority: 'high',
    text: 'Column differential pressure high — approaching flood',
    ...g(
      'Vapour load beyond what the trays can pass: too much charge, too much firing, too much reflux, or pressure too low.',
      'At flood the trays fill with liquid, separation collapses and every product goes off spec at once. Recovering takes hours.',
      'Back off the vapour load: reduce the coil outlet first, then the charge rate. Raising the pressure also helps — it shrinks the vapour volume.',
    ),
  },
  {
    id: 'LT-302.L',
    tag: 'LT-302.PV',
    kind: 'L',
    limit: 22,
    deadband: 2,
    onDelay: 20,
    priority: 'high',
    text: 'Reflux drum level low',
    ...g('Drawing more naphtha and reflux than the condenser is making.', 'Losing the drum loses the reflux pump — and losing reflux collapses the top of the tower within minutes.', 'Cut the naphtha product rate. Check the condenser is actually condensing.'),
  },
  {
    id: 'LT-302.H',
    tag: 'LT-302.PV',
    kind: 'H',
    limit: 82,
    deadband: 2,
    onDelay: 20,
    priority: 'medium',
    text: 'Reflux drum level high',
    ...g('More condensate than reflux plus product is taking away.', 'The drum floods and carries liquid into the vent.', 'Increase the naphtha product rate, or check LIC-302 is in AUTO.'),
  },
  {
    id: 'LT-307.H',
    tag: 'LT-307.PV',
    kind: 'H',
    limit: 82,
    deadband: 2,
    onDelay: 30,
    priority: 'medium',
    text: 'Column bottoms level high',
    ...g('The draws do not add up to the charge rate.', 'Liquid backs up into the stripping section and the bottom trays flood.', 'Increase the residue rate, or check the material balance across the whole unit.'),
  },
  {
    id: 'LT-307.L',
    tag: 'LT-307.PV',
    kind: 'L',
    limit: 20,
    deadband: 2,
    onDelay: 30,
    priority: 'high',
    text: 'Column bottoms level low',
    ...g('Drawing residue faster than it is being made.', 'Losing the bottoms level loses the seal to the residue pump and lets vapour through.', 'Cut the residue draw.'),
  },
  {
    id: 'AT-304.H',
    tag: 'AT-304.PV',
    kind: 'H',
    limit: 438,
    deadband: 3,
    onDelay: 120,
    priority: 'medium',
    text: 'Naphtha endpoint high — off spec',
    ...g('Not enough reflux, pressure too high, or the coil outlet too hot.', 'Heavy material in the naphtha; the downstream treater will reject it.', 'Increase reflux — that is what TIC-304 is for. Check the top temperature is on setpoint.'),
  },
  {
    id: 'AT-306.H',
    tag: 'AT-306.PV',
    kind: 'H',
    limit: 623,
    deadband: 3,
    onDelay: 120,
    priority: 'medium',
    text: 'Diesel 95 % point high — off spec',
    ...g('Drawing too much diesel, or the coil outlet too hot.', 'Off-spec diesel goes to slops or gets reprocessed. Either way it costs money.', 'Reduce the diesel draw rate a little and give it fifteen minutes.'),
  },
  {
    id: 'TIC-201.DEV',
    tag: 'TT-201.PV',
    kind: 'devLo',
    limit: 8,
    spTag: 'TIC-201.SP',
    deadband: 2,
    onDelay: 60,
    priority: 'medium',
    text: 'Heater coil outlet below setpoint',
    ...g('Fuel-gas pressure down, burners fouled, or the charge rate up without the firing following.', 'The tower under-flashes: light material stays in the residue and every yield falls.', 'Check the fuel header pressure and that FIC-202 is in CAS.'),
  },
  {
    id: 'AT-202.L',
    tag: 'AT-202.PV',
    kind: 'L',
    limit: 1.4,
    deadband: 0.3,
    onDelay: 30,
    priority: 'high',
    text: 'Heater excess oxygen low',
    ...g('Firing hard with the air registers where they were.', 'Incomplete combustion: carbon monoxide, afterburn in the convection section, and a real explosion risk.', 'Reduce firing. Air before fuel, always.'),
  },
];

export const interlocks: InterlockConfig[] = [
  {
    id: 'I-201',
    name: 'Heater trip — tube skin or low charge flow',
    description:
      'Cuts the fuel on tube-skin high-high or on low charge flow held for ten seconds. ' +
      'A fired coil with no flow through it is the classic tube rupture. Latching; reset ' +
      'once the skin is below 700 K and flow is restored.',
    cause: {
      op: 'or',
      terms: [
        { op: '>', tag: 'TT-202.PV', value: 753 },
        { op: 'for', term: { op: '<', tag: 'FT-101.PV', value: 220 }, seconds: 10 },
      ],
    },
    effects: [
      { do: 'fault', id: 'FLAME-OUT', on: true },
      { do: 'setMode', loop: 'FIC-202', mode: 'MAN' },
      { do: 'setOp', loop: 'FIC-202', value: 0 },
      { do: 'setMode', loop: 'TIC-201', mode: 'MAN' },
      { do: 'setOp', loop: 'TIC-201', value: 0 },
    ],
    latching: true,
    resetPermissive: {
      op: 'and',
      terms: [
        { op: '<', tag: 'TT-202.PV', value: 700 },
        { op: '>', tag: 'FT-101.PV', value: 280 },
      ],
    },
    priority: 'emergency',
    bypassable: false,
    firstOut: [
      { id: 'SKIN-HH', text: 'Tube-skin temperature high-high', cond: { op: '>', tag: 'TT-202.PV', value: 753 } },
      { id: 'FLOW-LOW', text: 'Crude charge flow low', cond: { op: '<', tag: 'FT-101.PV', value: 220 } },
    ],
    onReset: [{ do: 'fault', id: 'FLAME-OUT', on: false }],
  },
  {
    id: 'I-301',
    name: 'Column high-high pressure — cut the firing',
    description:
      'At 2.6 bara the heater firing is cut and the cooling-water valve is driven fully open. ' +
      'Reset below 2.0 bara.',
    cause: { op: 'for', term: { op: '>', tag: 'PT-301.PV', value: 2.6 }, seconds: 5 },
    effects: [
      { do: 'setMode', loop: 'TIC-201', mode: 'MAN' },
      { do: 'setOp', loop: 'TIC-201', value: 0 },
      { do: 'setMode', loop: 'FIC-202', mode: 'MAN' },
      { do: 'setOp', loop: 'FIC-202', value: 0 },
      { do: 'setMode', loop: 'PIC-301', mode: 'MAN' },
      { do: 'setOp', loop: 'PIC-301', value: 100 },
    ],
    latching: true,
    resetPermissive: { op: '<', tag: 'PT-301.PV', value: 2.0 },
    priority: 'emergency',
    bypassable: true,
    firstOut: [
      { id: 'P-HH', text: 'Column pressure high-high', cond: { op: '>', tag: 'PT-301.PV', value: 2.6 } },
      { id: 'CW-LOW', text: 'Condenser cooling-water flow low', cond: { op: '<', tag: 'FT-CW.PV', value: 120 } },
    ],
  },
];
